import { useTextePublic } from '../lib/textes-publics'
/**
 * Atterrissage du lien magique : `/portail/connexion/verifier?token=…`
 *
 * Le jeton est à **usage unique** et vaut 15 minutes : la vérification n’est
 * donc lancée qu’une seule fois, même sous `StrictMode` (double montage en
 * développement) — sinon le second appel consommerait un jeton déjà utilisé et
 * afficherait une erreur à un abonné pourtant légitime.
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'

import { messageErreur } from '../lib/api'
import { CHEMINS } from '../lib/chemins'
import { classesBouton } from '../ui/Button'
import { EmptyState } from '../ui/EmptyState'
import { ChargementPage } from '../ui/Spinner'
import { useAuth } from './useAuth'

type Etat = 'verification' | 'echec'

export function VerificationLienMagique() {
  const texteInterface = useTextePublic()

  const [parametres] = useSearchParams()
  const naviguer = useNavigate()
  const { verifierLienMagique } = useAuth()

  const [etat, setEtat] = useState<Etat>('verification')
  const [erreur, setErreur] = useState<string | null>(null)
  const dejaTente = useRef(false)

  const jeton = parametres.get('token') ?? ''

  useEffect(() => {
    if (dejaTente.current) return
    dejaTente.current = true

    if (jeton === '') {
      setErreur(texteInterface('Lien de connexion incomplet.'))
      setEtat('echec')
      return
    }

    void (async () => {
      try {
        await verifierLienMagique(jeton)
        await naviguer(CHEMINS.portail, { replace: true })
      } catch (cause) {
        setErreur(messageErreur(cause, texteInterface('Ce lien de connexion n’est plus valable.')))
        setEtat('echec')
      }
    })()
  }, [jeton, naviguer, verifierLienMagique])

  if (etat === 'verification') {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-slate-100 px-4">
        <ChargementPage libelle={texteInterface('Connexion en cours…')} />
      </main>
    )
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md rounded-carte border border-slate-200 bg-white shadow-sm">
        <EmptyState
          titre={texteInterface('Connexion impossible')}
          description={
            <>
              {erreur ?? texteInterface('Ce lien de connexion n’est plus valable.')}
              <br />{' '}
              {texteInterface(
                'Les liens expirent après 15 minutes et ne servent qu’une fois : demandez-en un nouveau.',
              )}{' '}
            </>
          }
          action={
            <Link to={CHEMINS.connexionAbonne} className={classesBouton('primaire')}>
              {' '}
              {texteInterface('Demander un nouveau lien')}{' '}
            </Link>
          }
        />
      </div>
    </main>
  )
}
